/**
 * Thin wrapper around `queryInteractiveFeatures` kept in its own module so
 * `useMapClickHandler.test.ts` can mock that import via module mocking.
 */
import { MapMouseEvent } from 'shared/map-exports';
import {
  ExternalFeatureLayer,
  FeatureWithEntityType,
} from 'features/mapping/mapping.types';
import {
  attachEntityTypes,
  getDisabledLayerIds,
  getExternalFeatureLayerListenerLayerId,
  queryInteractiveFeatures,
} from './interactiveFeatures';
import { getIsModeratingPhotoSubmission } from './featureClassification';

type GatherClickFeaturesConfig = {
  externalFeatureLayers: ExternalFeatureLayer[];
  showLocationV2: boolean;
  aqiStationsLayerEnabled: boolean;
  /** Some layers are only clickable on certain routes. */
  pathname: string;
  /** When off, `place` clicks fall back to the legacy POI dialog (`legacyPlace`). */
  pinDropImprovementsEnabled: boolean;
};

/** Downgrade a `place` entity type to the legacy POI-dialog type when the flag is off. */
const applyLegacyPlaceGate = (
  features: FeatureWithEntityType[],
  pinDropImprovementsEnabled: boolean,
): FeatureWithEntityType[] => {
  if (pinDropImprovementsEnabled) return features;
  return features.map((feature) =>
    feature.entityType === 'place'
      ? { ...feature, entityType: 'legacyPlace' }
      : feature,
  );
};

export const gatherClickFeatures = (
  event: MapMouseEvent,
  {
    externalFeatureLayers,
    showLocationV2,
    aqiStationsLayerEnabled,
    pathname,
    pinDropImprovementsEnabled,
  }: GatherClickFeaturesConfig,
): FeatureWithEntityType[] => {
  const dynamicLayerIds = externalFeatureLayers
    .map(getExternalFeatureLayerListenerLayerId)
    .filter(Boolean) as string[];

  const disabledLayerIds = getDisabledLayerIds({
    showLocationV2,
    aqiStationsLayerEnabled,
    isModeratingPhotoSubmission: getIsModeratingPhotoSubmission(pathname),
  });

  const interactiveFeatures = queryInteractiveFeatures(
    event,
    dynamicLayerIds,
    disabledLayerIds,
  );

  return applyLegacyPlaceGate(
    attachEntityTypes(interactiveFeatures, dynamicLayerIds),
    pinDropImprovementsEnabled,
  );
};
